import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { inventoryAPI, itemFilesAPI, programsAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { SearchableSelect } from '@/components/SearchableSelect';
import {
  ArrowLeft, Edit, Trash2, Package, Warehouse,
  History, Upload, Download, FileText, X, GitFork, Search,
  ChevronLeft, ChevronRight, Copy, FolderKanban, Plus, Link2,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { PageBreadcrumb } from '@/components/PageBreadcrumb';
import { AttachmentThumb } from '@/components/AttachmentThumb';

const TYPE_COLORS = {
  'Fixed Asset': 'bg-blue-50 text-blue-700 border-blue-200',
  'Raw Material': 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'Service': 'bg-purple-50 text-purple-700 border-purple-200',
  'Consumable': 'bg-amber-50 text-amber-700 border-amber-200',
  'Semi-Finished Part': 'bg-cyan-50 text-cyan-700 border-cyan-200',
  'Finished Good': 'bg-teal-50 text-teal-700 border-teal-200',
  'Scrap': 'bg-slate-50 text-slate-600 border-slate-200',
};

export default function ItemDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [item, setItem] = useState(null);
  const [changelog, setChangelog] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  // Stock transaction history modal
  const [stockHistoryOpen, setStockHistoryOpen] = useState(false);
  const [stockHistoryGodown, setStockHistoryGodown] = useState(null);
  const [stockHistoryTxns, setStockHistoryTxns] = useState([]);
  const [stockHistoryTotal, setStockHistoryTotal] = useState(0);
  const [stockHistoryPage, setStockHistoryPage] = useState(1);
  const [stockHistoryLoading, setStockHistoryLoading] = useState(false);
  const [stockDateFrom, setStockDateFrom] = useState('');
  const [stockDateTo, setStockDateTo] = useState('');

  // Linked programs
  const [linkedPrograms, setLinkedPrograms] = useState([]);
  const [showLinkProgram, setShowLinkProgram] = useState(false);
  const [allPrograms, setAllPrograms] = useState([]);
  const [linkForm, setLinkForm] = useState({ program_id: '', item_type: 'sale', quantity: 0, unit_price: 0 });
  const [stockLots, setStockLots] = useState([]);
  useEffect(() => { loadItem(); loadChangelog(); loadFiles(); loadLinkedPrograms(); loadStockLots(); }, [id]);

  const loadItem = async () => {
    try {
      const res = await inventoryAPI.getItem(id);
      setItem(res.data);
    } catch (err) {
      toast.error('Item not found');
      navigate('/inventory/items');
    } finally { setLoading(false); }
  };

  const loadLinkedPrograms = async () => {
    try {
      const res = await inventoryAPI.getItemLinkedPrograms(id);
      setLinkedPrograms(res.data);
    } catch {}
  };

  const loadStockLots = async () => {
    try {
      const res = await inventoryAPI.getStockLots(id);
      setStockLots(res.data);
    } catch {}
  };

  const loadChangelog = async () => {
    try { const res = await inventoryAPI.getItemChangelog(id); setChangelog(res.data); } catch {}
  };

  const loadFiles = async () => {
    try { const res = await itemFilesAPI.list(id); setFiles(res.data); } catch {}
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    try {
      await inventoryAPI.deleteItem(id);
      toast.success('Item deleted');
      navigate('/inventory/items');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('description', file.name);
      await itemFilesAPI.upload(id, formData);
      toast.success('File uploaded');
      loadFiles();
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const handleFileDownload = async (file) => {
    try {
      const res = await itemFilesAPI.download(id, file.id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', file.original_name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  const handleFileDelete = async (fileId) => {
    if (!window.confirm('Delete this file?')) return;
    try {
      await itemFilesAPI.delete(id, fileId);
      toast.success('File deleted');
      loadFiles();
    } catch { toast.error('Failed to delete file'); }
  };

  const handleLinkToProgram = async () => {
    if (!linkForm.program_id) { toast.error('Select a program'); return; }
    try {
      await inventoryAPI.linkItemToProgram(id, linkForm);
      toast.success('Item linked to program');
      setShowLinkProgram(false);
      setLinkForm({ program_id: '', item_type: 'sale', quantity: 0, unit_price: 0 });
      loadLinkedPrograms();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to link'); }
  };

  const openLinkDialog = async () => {
    try {
      const res = await programsAPI.list({ limit: 200 });
      setAllPrograms(res.data.programs || res.data || []);
    } catch {}
    setShowLinkProgram(true);
  };

  const openStockHistory = (balance) => {
    setStockHistoryGodown(balance);
    setStockHistoryPage(1);
    setStockDateFrom('');
    setStockDateTo('');
    setStockHistoryOpen(true);
    loadStockHistory(balance.godown_id, 1, '', '');
  };

  const loadStockHistory = async (godownId, pg = 1, from = '', to = '') => {
    setStockHistoryLoading(true);
    try {
      const params = { item_id: id, godown_id: godownId, page: pg, limit: 25 };
      if (from) params.from_date = from;
      if (to) params.to_date = to;
      const res = await inventoryAPI.listTransactions(params);
      setStockHistoryTxns(res.data.transactions || []);
      setStockHistoryTotal(res.data.total || 0);
    } catch { toast.error('Failed to load history'); }
    finally { setStockHistoryLoading(false); }
  };

  const handleStockHistoryFilter = () => {
    if (stockHistoryGodown) {
      setStockHistoryPage(1);
      loadStockHistory(stockHistoryGodown.godown_id, 1, stockDateFrom, stockDateTo);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-sm text-muted-foreground">Loading...</div>;
  if (!item) return null;

  const totalStock = (item.balances || []).reduce((sum, b) => sum + (b.quantity || 0), 0);
  const formatSize = (bytes) => bytes > 1048576 ? `${(bytes / 1048576).toFixed(1)} MB` : `${(bytes / 1024).toFixed(0)} KB`;

  return (
    <div className="space-y-4 p-4 md:p-6" data-testid="item-detail-page">
      <PageBreadcrumb 
        currentPageLabel={item?.unique_id || 'Item Details'}
        customItems={[
          { path: '/inventory', label: 'Inventory', isLast: false },
          { path: '/inventory/items', label: 'Item Master', isLast: false },
          { path: `/inventory/items/${id}`, label: item?.unique_id || 'Details', isLast: true },
        ]}
      />
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="font-heading text-xl font-bold tracking-tight">{item.item_name}</h1>
              <Badge variant="outline" className={TYPE_COLORS[item.item_type]}>{item.item_type}</Badge>
            </div>
            <p className="text-xs text-muted-foreground font-mono">{item.unique_id}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => navigate(`/inventory/items/new?copyFrom=${id}`)} data-testid="copy-item-btn" title="Create copy of this item">
            <Copy className="w-3.5 h-3.5 mr-1" /> Copy
          </Button>
          <Button size="sm" variant="outline" onClick={() => navigate(`/inventory/items/${id}/edit`)} data-testid="edit-item-btn">
            <Edit className="w-3.5 h-3.5 mr-1" /> Edit
          </Button>
          {isAdmin && (
            <Button size="sm" variant="destructive" onClick={handleDelete} data-testid="delete-item-btn">
              <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
            </Button>
          )}
        </div>
      </div>

      <Tabs defaultValue="info">
        <TabsList>
          <TabsTrigger value="info" data-testid="tab-info">Details</TabsTrigger>
          <TabsTrigger value="stock" data-testid="tab-stock">Stock ({totalStock})</TabsTrigger>
          <TabsTrigger value="transactions" data-testid="tab-transactions">Transactions</TabsTrigger>
          <TabsTrigger value="files" data-testid="tab-files">Files ({files.length})</TabsTrigger>
          <TabsTrigger value="bom" data-testid="tab-bom">BOM/Routes ({(item.bom_routes || []).length})</TabsTrigger>
          <TabsTrigger value="lots" data-testid="tab-lots">FIFO Lots ({stockLots.length})</TabsTrigger>
          <TabsTrigger value="programs" data-testid="tab-programs">Programs ({linkedPrograms.length})</TabsTrigger>
          {item.is_cutting_tool && <TabsTrigger value="tool-history" data-testid="tab-tool-history">Tool History</TabsTrigger>}
          <TabsTrigger value="changelog" data-testid="tab-changelog">Changelog</TabsTrigger>
        </TabsList>

        {/* Info Tab */}
        <TabsContent value="info" className="space-y-4 mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-xs font-medium text-muted-foreground">Basic Info</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                <InfoRow label="Item Name" value={item.item_name} />
                <InfoRow label="Unique ID" value={item.unique_id} mono />
                <InfoRow label="External ID" value={item.external_id} />
                <InfoRow label="Type" value={item.item_type} />
                <InfoRow label="Category" value={item.category} />
                <InfoRow label="Primary UOM" value={item.unit_of_measure} />
                {item.secondary_uom && (
                  <>
                    <InfoRow label="Secondary UOM" value={item.secondary_uom} />
                    <InfoRow label="Conversion" value={`1 ${item.unit_of_measure} = ${item.conversion_factor} ${item.secondary_uom}`} />
                  </>
                )}
                <InfoRow label="Description" value={item.description} />
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-xs font-medium text-muted-foreground">Technical</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                <InfoRow label="Drawing No." value={item.drawing_number} mono />
                <InfoRow label="Revision" value={item.revision} />
                <InfoRow label="Material Spec" value={item.material_spec} />
                <InfoRow label="HSN Code" value={item.hsn_code} mono />
                <InfoRow label="Customer" value={item.customer_name} />
                <InfoRow label="Make/Buy" value={item.make_or_buy} />
                <InfoRow label="Preferred Supplier" value={item.preferred_supplier_name} />
                <InfoRow label="Inventory Item" value={item.is_inventory_item ? 'Yes' : 'No'} />
                {item.is_inventory_item && <>
                  <InfoRow label="Min Stock" value={item.min_stock_level} />
                  <InfoRow label="Reorder Level" value={item.reorder_level} />
                </>}
                {item.is_cutting_tool && <>
                  <InfoRow label="Cutting Tool" value="Yes" />
                  <InfoRow label="Max Regrinds" value={item.max_regrinds} />
                </>}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Stock Tab */}
        <TabsContent value="stock" className="mt-4">
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium">Stock by Godown</CardTitle>
                <Badge className="text-xs">Total: {totalStock} {item.unit_of_measure}</Badge>
              </div>
            </CardHeader>
            <CardContent>
              {(!item.balances || item.balances.length === 0) ? (
                <p className="text-xs text-muted-foreground text-center py-6">No stock recorded</p>
              ) : (
                <div className="space-y-2">
                  {item.balances.map(b => (
                    <div key={b.id || b.godown_id}
                      className="flex items-center justify-between p-2.5 rounded-md border bg-muted/30 hover:bg-muted/50 cursor-pointer transition-colors"
                      onClick={() => openStockHistory(b)}
                      data-testid={`stock-row-${b.godown_id}`}
                      title="Click to view transaction history"
                    >
                      <div className="flex items-center gap-2">
                        <Warehouse className="w-4 h-4 text-muted-foreground" />
                        <span className="text-xs font-medium">{b.godown_name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-bold text-blue-600 underline decoration-dotted underline-offset-2">{b.quantity} {item.unit_of_measure}</span>
                        {item.secondary_uom && item.conversion_factor > 0 && (
                          <span className="text-xs text-muted-foreground">/ {(b.quantity * item.conversion_factor).toFixed(2)} {item.secondary_uom}</span>
                        )}
                        <History className="w-3.5 h-3.5 text-muted-foreground" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Transactions Tab */}
        <TabsContent value="transactions" className="mt-4">
          <Card>
            <CardContent className="p-0">
              {(!item.transactions || item.transactions.length === 0) ? (
                <p className="text-xs text-muted-foreground text-center py-6">No transactions</p>
              ) : (
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left p-2.5 font-medium text-muted-foreground">Date</th>
                      <th className="text-left p-2.5 font-medium text-muted-foreground">Type</th>
                      <th className="text-left p-2.5 font-medium text-muted-foreground">Qty</th>
                      <th className="text-left p-2.5 font-medium text-muted-foreground">Godown</th>
                      <th className="text-left p-2.5 font-medium text-muted-foreground">Ref</th>
                      <th className="text-left p-2.5 font-medium text-muted-foreground">By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {item.transactions.map(txn => (
                      <tr key={txn.id} className="border-b">
                        <td className="p-2.5 font-mono">{txn.date}</td>
                        <td className="p-2.5">
                          <Badge variant="outline" className={`text-[10px] ${
                            ['receive', 'return', 'regrind_receive', 'production_receive', 'transfer_in', 'discrepancy_excess'].includes(txn.transaction_type) ? 'text-emerald-700 bg-emerald-50' :
                            ['issue', 'regrind_send', 'scrap', 'return_to_supplier', 'transfer_out', 'discrepancy_short'].includes(txn.transaction_type) ? 'text-red-700 bg-red-50' :
                            'text-amber-700'}`}>
                            {txn.transaction_type.replace(/_/g, ' ')}
                          </Badge>
                        </td>
                        <td className="p-2.5 font-medium font-mono">
                          {['receive', 'return', 'regrind_receive', 'production_receive', 'transfer_in', 'discrepancy_excess'].includes(txn.transaction_type)
                            ? <span className="text-emerald-600">+{txn.quantity}</span>
                            : ['issue', 'regrind_send', 'scrap', 'return_to_supplier', 'transfer_out', 'discrepancy_short'].includes(txn.transaction_type)
                            ? <span className="text-red-600">-{txn.quantity}</span>
                            : txn.quantity}
                        </td>
                        <td className="p-2.5">{txn.godown_name}</td>
                        <td className="p-2.5 text-muted-foreground">{txn.reference_number || '-'}</td>
                        <td className="p-2.5 text-muted-foreground">{txn.created_by_name}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Files Tab */}
        <TabsContent value="files" className="mt-4">
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-sm font-medium">Drawings, Images &amp; CAD Files</CardTitle>
                  <p className="text-[10px] text-muted-foreground mt-0.5">PDF, images &amp; CAD (dwg, dxf, step, stp, iges, stl, sldprt…) · up to 50MB · stored securely, persists across deployments</p>
                </div>
                <div>
                  <input type="file" id="file-upload" className="hidden" accept=".pdf,.png,.jpg,.jpeg,.webp,.gif,.bmp,.tif,.tiff,.dwg,.dxf,.step,.stp,.iges,.igs,.stl,.sldprt,.sldasm,.slddrw,.ipt,.iam,.idw,.catpart,.catproduct,.prt,.asm,.x_t,.x_b,.3mf,.obj,.sat,.jt,.3dm,.f3d,.zip" onChange={handleFileUpload} data-testid="file-upload-input" />
                  <Button size="sm" variant="outline" onClick={() => document.getElementById('file-upload').click()} disabled={uploading} data-testid="upload-file-btn">
                    <Upload className="w-3.5 h-3.5 mr-1" /> {uploading ? 'Uploading...' : 'Upload File'}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {files.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-6">No files attached. Upload the part drawing (PDF), reference images, or CAD data (STEP/DWG/etc.).</p>
              ) : (
                <div className="space-y-2">
                  {files.map(f => (
                    <div key={f.id} className="flex items-center justify-between p-2.5 rounded-md border bg-muted/30" data-testid={`file-row-${f.id}`}>
                      <div className="flex items-center gap-3 min-w-0">
                        <AttachmentThumb itemId={id} file={f} size="md" onClick={() => handleFileDownload(f)} className="flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-xs font-medium truncate">{f.original_name}</p>
                          <p className="text-[10px] text-muted-foreground">
                            {formatSize(f.size)} &middot; {f.uploaded_by_name} &middot; {new Date(f.uploaded_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => handleFileDownload(f)} title="Download" data-testid={`download-file-${f.id}`}>
                          <Download className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500 hover:text-red-700" onClick={() => handleFileDelete(f.id)} title="Delete" data-testid={`delete-file-${f.id}`}>
                          <X className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tool History Tab */}
        {item.is_cutting_tool && (
          <TabsContent value="tool-history" className="mt-4">
            <Card>
              <CardContent className="p-0">
                {(!item.tool_history || item.tool_history.length === 0) ? (
                  <p className="text-xs text-muted-foreground text-center py-6">No tool tracking history</p>
                ) : (
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="text-left p-2.5 font-medium text-muted-foreground">Tool ID</th>
                        <th className="text-left p-2.5 font-medium text-muted-foreground">Regrind Count</th>
                        <th className="text-left p-2.5 font-medium text-muted-foreground">Status</th>
                        <th className="text-left p-2.5 font-medium text-muted-foreground">Last Regrind</th>
                      </tr>
                    </thead>
                    <tbody>
                      {item.tool_history.map(th => (
                        <tr key={th.tool_unique_id || th.id} className="border-b">
                          <td className="p-2.5 font-mono font-medium">{th.tool_unique_id}</td>
                          <td className="p-2.5">{th.regrind_count || 0} / {item.max_regrinds}</td>
                          <td className="p-2.5">
                            <Badge variant="outline" className={th.status === 'scrapped' ? 'text-red-600' : 'text-emerald-600'}>
                              {th.status}
                            </Badge>
                          </td>
                          <td className="p-2.5">{th.last_regrind_date || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* BOM / Routes Tab */}
        <TabsContent value="bom" className="mt-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Routes & Bills of Materials</CardTitle>
            </CardHeader>
            <CardContent>
              {(!item.bom_routes || item.bom_routes.length === 0) ? (
                <p className="text-xs text-muted-foreground text-center py-6">No routes or BOMs reference this item</p>
              ) : (
                <div className="space-y-3">
                  {item.bom_routes.map(br => (
                    <Link
                      key={br.id}
                      to={`/production/routes/${br.id}`}
                      className="block p-3 rounded-md border bg-muted/30 hover:bg-muted/50 transition-colors"
                      data-testid={`bom-route-${br.id}`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-semibold">{br.route_name}</span>
                        <div className="flex gap-1">
                          {(br.roles || []).map(role => (
                            <Badge key={role} variant="outline" className={`text-[9px] ${
                              role === 'primary_output' ? 'text-emerald-700 border-emerald-200' :
                              role === 'input_material' ? 'text-blue-700 border-blue-200' :
                              role === 'scrap' ? 'text-amber-700 border-amber-200' : ''
                            }`}>
                              {role.replace(/_/g, ' ')}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      {br.primary_item_name && (
                        <p className="text-[10px] text-muted-foreground">
                          Primary Output: {br.primary_item_name} ({br.primary_item_unique_id}) | Rev: {br.revision || '0'}
                        </p>
                      )}
                      <div className="flex gap-4 mt-1 text-[10px] text-muted-foreground">
                        <span>BOM Inputs: {(br.bill_of_materials || []).length}</span>
                        <span>Outputs: {(br.output_items || []).length}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>


        {/* FIFO Lots Tab */}
        <TabsContent value="lots" className="mt-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-1.5"><Warehouse className="w-4 h-4" /> Stock Lots (FIFO)</CardTitle>
              <p className="text-[10px] text-muted-foreground">Items are consumed oldest-first. Oldest lots at the top.</p>
            </CardHeader>
            <CardContent>
              {stockLots.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-6">No active stock lots</p>
              ) : (
                <div className="border rounded overflow-hidden">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-slate-50 border-b text-[10px] text-muted-foreground">
                        <th className="text-left p-2.5 font-medium">Received Date</th>
                        <th className="text-left p-2.5 font-medium">Age (Days)</th>
                        <th className="text-right p-2.5 font-medium">Original Qty</th>
                        <th className="text-right p-2.5 font-medium">Remaining</th>
                        {item.secondary_uom && <th className="text-right p-2.5 font-medium">Remaining ({item.secondary_uom})</th>}
                        <th className="text-right p-2.5 font-medium">Consumed %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stockLots.map((lot, i) => {
                        const ageDays = Math.floor((Date.now() - new Date(lot.received_date).getTime()) / 86400000);
                        const consumedPct = lot.original_quantity > 0 ? Math.round((1 - lot.remaining_quantity / lot.original_quantity) * 100) : 0;
                        return (
                          <tr key={lot.id} className={`border-b last:border-0 ${i === 0 ? 'bg-amber-50/50' : ''}`} data-testid={`lot-row-${lot.id}`}>
                            <td className="p-2.5 font-mono">{lot.received_date}</td>
                            <td className="p-2.5">
                              <span className={`font-semibold ${ageDays > 180 ? 'text-red-600' : ageDays > 90 ? 'text-amber-600' : 'text-slate-700'}`}>
                                {ageDays}d
                              </span>
                              {i === 0 && <span className="ml-1 text-[9px] text-amber-600 font-medium">(next to consume)</span>}
                            </td>
                            <td className="p-2.5 text-right font-mono text-muted-foreground">{lot.original_quantity} {item.unit_of_measure}</td>
                            <td className="p-2.5 text-right font-mono font-semibold">{lot.remaining_quantity} {item.unit_of_measure}</td>
                            {item.secondary_uom && (
                              <td className="p-2.5 text-right font-mono text-muted-foreground">{(lot.remaining_quantity * (item.conversion_factor || 0)).toFixed(2)} {item.secondary_uom}</td>
                            )}
                            <td className="p-2.5 text-right">
                              <div className="flex items-center justify-end gap-1.5">
                                <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                  <div className="h-full bg-blue-500 rounded-full" style={{ width: `${consumedPct}%` }} />
                                </div>
                                <span className="text-[10px] font-mono w-8 text-right">{consumedPct}%</span>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr className="bg-slate-50 border-t font-medium">
                        <td className="p-2.5" colSpan={2}>Total ({stockLots.length} lot{stockLots.length !== 1 ? 's' : ''})</td>
                        <td className="p-2.5 text-right font-mono">{stockLots.reduce((s, l) => s + l.original_quantity, 0).toFixed(2)}</td>
                        <td className="p-2.5 text-right font-mono font-semibold">{stockLots.reduce((s, l) => s + l.remaining_quantity, 0).toFixed(2)}</td>
                        {item.secondary_uom && (
                          <td className="p-2.5 text-right font-mono">{(stockLots.reduce((s, l) => s + l.remaining_quantity, 0) * (item.conversion_factor || 0)).toFixed(2)}</td>
                        )}
                        <td />
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>



        {/* Programs Tab */}
        <TabsContent value="programs" className="mt-4">
          <Card>
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <CardTitle className="text-sm font-medium flex items-center gap-1.5"><FolderKanban className="w-4 h-4" /> Linked Programs</CardTitle>
              <Button size="sm" variant="outline" onClick={openLinkDialog} data-testid="link-to-program-btn">
                <Plus className="w-3 h-3 mr-1" /> Link to Program
              </Button>
            </CardHeader>
            <CardContent>
              {linkedPrograms.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-6">This item is not linked to any program</p>
              ) : (
                <div className="space-y-2">
                  {linkedPrograms.map(p => (
                    <div key={p.program_id} className="flex items-center justify-between p-2.5 rounded border bg-muted/30 hover:bg-muted/50 cursor-pointer" onClick={() => navigate(`/programs/${p.program_id}`)} data-testid={`linked-program-${p.program_id}`}>
                      <div>
                        <p className="text-xs font-medium">{p.program_name} <span className="font-mono text-muted-foreground ml-1">{p.program_number}</span></p>
                        <p className="text-[10px] text-muted-foreground">{p.customer_name} — {p.linked_items.length} link(s)</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-[10px]">{p.status}</Badge>
                        <Link2 className="w-3 h-3 text-muted-foreground" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>


        {/* Changelog Tab */}
        <TabsContent value="changelog" className="mt-4">
          <Card>
            <CardContent className="p-0">
              {changelog.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-6">No changes recorded</p>
              ) : (
                <div className="divide-y">
                  {changelog.map(log => (
                    <div key={log.id} className="p-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium">{log.changed_by_name}</span>
                        <span className="text-[10px] text-muted-foreground font-mono">{new Date(log.changed_at).toLocaleString()}</span>
                      </div>
                      <ul className="space-y-0.5">
                        {log.changes.map((c, i) => (
                          <li key={i} className="text-[11px] text-muted-foreground">{c}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Stock Transaction History Modal */}
      <Dialog open={stockHistoryOpen} onOpenChange={setStockHistoryOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-sm">
              <History className="w-4 h-4" />
              Transaction History: {stockHistoryGodown?.godown_name}
            </DialogTitle>
            <DialogDescription>
              {item?.item_name} — Current Balance: {stockHistoryGodown?.quantity} {item?.unit_of_measure}
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 items-end">
            <div>
              <label className="text-[10px] text-muted-foreground">From</label>
              <Input type="date" value={stockDateFrom} onChange={e => setStockDateFrom(e.target.value)}
                className="h-7 text-xs w-36" data-testid="stock-history-from" />
            </div>
            <div>
              <label className="text-[10px] text-muted-foreground">To</label>
              <Input type="date" value={stockDateTo} onChange={e => setStockDateTo(e.target.value)}
                className="h-7 text-xs w-36" data-testid="stock-history-to" />
            </div>
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={handleStockHistoryFilter} data-testid="stock-history-filter-btn">
              <Search className="w-3 h-3 mr-1" /> Filter
            </Button>
            {(stockDateFrom || stockDateTo) && (
              <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => {
                setStockDateFrom(''); setStockDateTo('');
                if (stockHistoryGodown) loadStockHistory(stockHistoryGodown.godown_id, 1, '', '');
              }}>Clear</Button>
            )}
          </div>
          {stockHistoryLoading ? (
            <p className="text-xs text-muted-foreground text-center py-6">Loading...</p>
          ) : stockHistoryTxns.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-6">No transactions found</p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="stock-history-table">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left p-2 font-medium text-muted-foreground">Date</th>
                      <th className="text-left p-2 font-medium text-muted-foreground">Type</th>
                      <th className="text-right p-2 font-medium text-muted-foreground">Qty</th>
                      <th className="text-left p-2 font-medium text-muted-foreground">Reference</th>
                      <th className="text-left p-2 font-medium text-muted-foreground">Remarks</th>
                      <th className="text-left p-2 font-medium text-muted-foreground">By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stockHistoryTxns.map(txn => {
                      const isAddition = ['receive', 'production_receive', 'return', 'scrap_receive', 'qc_reject_in', 'regrind_receive'].includes(txn.transaction_type);
                      return (
                        <tr key={txn.id} className="border-b" data-testid={`txn-row-${txn.id}`}>
                          <td className="p-2 font-mono">{txn.date}</td>
                          <td className="p-2">
                            <Badge variant="outline" className={`text-[9px] ${isAddition ? 'text-emerald-700 border-emerald-200' : 'text-red-700 border-red-200'}`}>
                              {isAddition ? '+' : '-'} {txn.transaction_type.replace(/_/g, ' ')}
                            </Badge>
                          </td>
                          <td className={`p-2 text-right font-medium ${isAddition ? 'text-emerald-700' : 'text-red-700'}`}>
                            {isAddition ? '+' : '-'}{txn.quantity}
                          </td>
                          <td className="p-2 text-muted-foreground font-mono">{txn.reference_number || '-'}</td>
                          <td className="p-2 text-muted-foreground max-w-[150px] truncate" title={txn.remarks}>{txn.remarks || '-'}</td>
                          <td className="p-2 text-muted-foreground">{txn.created_by_name}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {Math.ceil(stockHistoryTotal / 25) > 1 && (
                <div className="flex items-center justify-between pt-2">
                  <p className="text-[10px] text-muted-foreground">Page {stockHistoryPage} of {Math.ceil(stockHistoryTotal / 25)} ({stockHistoryTotal} transactions)</p>
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" disabled={stockHistoryPage <= 1} className="h-6 text-[10px]"
                      onClick={() => { const p = stockHistoryPage - 1; setStockHistoryPage(p); loadStockHistory(stockHistoryGodown.godown_id, p, stockDateFrom, stockDateTo); }}>
                      <ChevronLeft className="w-3 h-3" /> Prev
                    </Button>
                    <Button size="sm" variant="outline" disabled={stockHistoryPage >= Math.ceil(stockHistoryTotal / 25)} className="h-6 text-[10px]"
                      onClick={() => { const p = stockHistoryPage + 1; setStockHistoryPage(p); loadStockHistory(stockHistoryGodown.godown_id, p, stockDateFrom, stockDateTo); }}>
                      Next <ChevronRight className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Link to Program Dialog */}
      <Dialog open={showLinkProgram} onOpenChange={setShowLinkProgram}>
        <DialogContent>
          <DialogHeader><DialogTitle className="text-sm flex items-center gap-2"><FolderKanban className="w-4 h-4" /> Link Item to Program</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Select Program *</Label>
              <SearchableSelect
                options={allPrograms.map(p => ({ value: p.id, label: `${p.program_name} (${p.program_number})`, sub: p.customer_name || '' }))}
                value={linkForm.program_id}
                onSelect={v => setLinkForm(f => ({ ...f, program_id: v }))}
                placeholder="Search programs..."
                emptyText="No programs found"
                testId="link-program-select"
              />
            </div>
            <div>
              <Label className="text-xs">Item Type</Label>
              <Select value={linkForm.item_type} onValueChange={v => setLinkForm(f => ({ ...f, item_type: v }))}>
                <SelectTrigger className="h-8 text-xs mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="sale">Sale Item</SelectItem>
                  <SelectItem value="purchase">Cost/Purchase Item</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Quantity</Label><Input type="number" className="h-8 text-xs mt-1" value={linkForm.quantity} onChange={e => setLinkForm(f => ({ ...f, quantity: parseFloat(e.target.value) || 0 }))} data-testid="link-qty" /></div>
              <div><Label className="text-xs">Unit Price</Label><Input type="number" className="h-8 text-xs mt-1" value={linkForm.unit_price} onChange={e => setLinkForm(f => ({ ...f, unit_price: parseFloat(e.target.value) || 0 }))} data-testid="link-price" /></div>
            </div>
            <Button size="sm" className="w-full" onClick={handleLinkToProgram} data-testid="confirm-link-program">Link to Program</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function InfoRow({ label, value, mono }) {
  return (
    <div className="flex items-baseline justify-between py-0.5">
      <span className="text-[11px] text-muted-foreground">{label}</span>
      <span className={`text-xs font-medium text-right ${mono ? 'font-mono' : ''}`}>{value || '-'}</span>
    </div>
  );
}
